/**
 * ReviewStep — read-only summary with per-section "Edit" links.
 *
 * Shows profile fields + verified contact info. Each section has an
 * "Edit" button that navigates back to the relevant step (pre-filled).
 *
 * Submit: POST /sellers/register/{id}/submit → `onSubmitted(hours)`.
 * Tokens: `--g-*` only. aria-live on error. ≥44px tap targets.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { SELLER_REG_NS } from "@/features/seller-registration/api";
import type { ProfileFormValues } from "@/features/seller-registration/schemas";
import { ApiError } from "@/lib/api-client";

const STEP_H1_CLASS =
  "text-[27px] font-bold leading-tight tracking-[-0.03em] text-foreground";

export interface ReviewStepProps {
  applicationId: string;
  email: string;
  profileData: ProfileFormValues;
  /** Display name for the selected market (slug or name). */
  marketDisplayName: string;
  apiRegisterSubmit: (applicationId: string) => Promise<{
    ok: boolean;
    status: string;
    estimated_review_hours: number;
  }>;
  onSubmitted: (estimatedHours: number) => void;
  onEditProfile: () => void;
}

function ReviewRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}): ReactElement | null {
  if (!value) return null;
  return (
    <div className="flex gap-2 py-1">
      <span className="min-w-[100px] shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

export function ReviewStep({
  applicationId,
  email,
  profileData,
  marketDisplayName,
  apiRegisterSubmit,
  onSubmitted,
  onEditProfile,
}: ReviewStepProps): ReactElement {
  const { t } = useTranslation(SELLER_REG_NS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    setErrorKey(null);
    try {
      const res = await apiRegisterSubmit(applicationId);
      onSubmitted(res.estimated_review_hours);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setErrorKey(code ? `errors.${String(code)}` : "errors.generic");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 py-2">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className={STEP_H1_CLASS} data-testid="review-h1">
          {t("review.h1")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("review.lede")}</p>
      </div>

      {/* Profile section */}
      <section
        aria-labelledby="review-profile-heading"
        className="rounded-[var(--g-radius)] border border-[var(--g-divider)] bg-[var(--g-surface)] p-4"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2
            id="review-profile-heading"
            className="text-sm font-semibold text-foreground"
          >
            {t("review.sectionProfile")}
          </h2>
          <button
            type="button"
            onClick={onEditProfile}
            data-testid="review-edit-profile"
            className={[
              "min-h-[44px] px-3 text-sm font-medium text-primary",
              "underline-offset-4 hover:underline",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            ].join(" ")}
          >
            {t("cta.editSection")}
          </button>
        </div>
        <div className="divide-y divide-[var(--g-divider)]">
          <ReviewRow label={t("review.fieldBusiness")} value={profileData.business_name} />
          <ReviewRow label={t("review.fieldMarket")} value={marketDisplayName} />
          <ReviewRow label={t("review.fieldPavilion")} value={profileData.pavilion_number} />
          <ReviewRow label={t("review.fieldEntrance")} value={profileData.entrance_hint || undefined} />
        </div>
      </section>

      {/* Contacts section */}
      <section
        aria-labelledby="review-contacts-heading"
        className="rounded-[var(--g-radius)] border border-[var(--g-divider)] bg-[var(--g-surface)] p-4"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2
            id="review-contacts-heading"
            className="text-sm font-semibold text-foreground"
          >
            {t("review.sectionContacts")}
          </h2>
          <button
            type="button"
            onClick={onEditProfile}
            data-testid="review-edit-contacts"
            className={[
              "min-h-[44px] px-3 text-sm font-medium text-primary",
              "underline-offset-4 hover:underline",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            ].join(" ")}
          >
            {t("cta.editSection")}
          </button>
        </div>
        <div className="divide-y divide-[var(--g-divider)]">
          <ReviewRow label={t("review.fieldEmail")} value={email} />
          <ReviewRow label={t("review.fieldPhone")} value={profileData.phone_e164} />
          <ReviewRow label={t("review.fieldWhatsapp")} value={profileData.whatsapp_phone_e164 || undefined} />
          <ReviewRow label={t("review.fieldTelegram")} value={profileData.telegram_handle || undefined} />
        </div>
      </section>

      {/* Error */}
      {errorKey ? (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
          data-testid="review-error"
        >
          <p className="text-sm text-destructive">{t(errorKey)}</p>
        </div>
      ) : null}

      {/* Submit CTA */}
      <div className="mt-auto pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-testid="review-submit"
          className={[
            "flex h-[44px] w-full items-center justify-center gap-2",
            "rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold",
            "text-[var(--g-on-primary)] shadow-[var(--g-shadow-sm)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "focus-visible:ring-offset-2 disabled:opacity-60",
          ].join(" ")}
        >
          {isSubmitting ? (
            <Loader2 size={16} aria-hidden="true" className="animate-spin" />
          ) : null}
          {isSubmitting ? t("cta.submitting") : t("cta.submit")}
        </button>
      </div>
    </div>
  );
}
